import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { DrawerComponent } from '@progress/kendo-angular-layout';
import { forkJoin, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { ReportPrefillComponent } from 'src/app/components/shared/investigations/investigation-documents/report-prefill/report-prefill.component';
import { investigationReportsCreate, investigationReportsDelete, investigationReportsDeleteOwn, investigationReportsList } from 'src/app/shared/helpers/auth-config/investigations.config';
import { sortReportTypes } from 'src/app/shared/helpers/helper';
import { DocumentCategories } from 'src/app/shared/modules/graphql/constants/document.constants';
import { SortOrder } from 'src/app/shared/modules/graphql/enums/generic.enums';
import { IApiDocument, IApiDocumentFilterType, IApiDocumentOrderBy, IApiDocumentType, IApiDocumentTypeOrderBy, IApiInvestigation, IApiInvestigationReportStatus, IApiInvestigationUpdateCategories, IApiInvestigationUpdateTypes, IApiUser } from 'src/app/shared/modules/graphql/types/types';
import { LoaderService } from 'src/app/shared/modules/loader/loader.service';
import { NotesModalComponent } from 'src/app/shared/modules/notes/notes-modal/notes-modal.component';
import { NotificationsService } from 'src/app/shared/modules/notifications/notifications.service';
import { unwrapConnection } from 'src/app/shared/rxjs.pipes';
import { AuthService, DocumentService, DocumentTypeService, InvestigationReportService, InvestigationService } from 'src/app/shared/services';

@Component({
  selector: 'app-mobile-investigation-documents',
  templateUrl: './mobile-investigation-documents.component.html',
  styleUrls: ['./mobile-investigation-documents.component.scss']
})
export class MobileInvestigationDocumentsComponent implements OnInit {
  @ViewChild("drawer") drawer: DrawerComponent;

  public investigation: IApiInvestigation;
  public reports: IApiDocument[] = [];
  public authConfig = {
    investigationReportsList,
    investigationReportsCreate,
    investigationReportsDelete,
    investigationReportsDeleteOwn
  }
  public selectedReportTypeId = '';
  public reportTypes: IApiDocumentType[] = [];

  private get requiredPreFillReport(): boolean {
    return this.reportTypes
      ?.some(r =>
        ['PIC-No Report Requested', 'PIC-Report to Follow', 'Post Inspection Communication'].includes(r?.name) &&
        r?.id === this.selectedReportTypeId
      ) ?? false;
  }

  public get IInvestigationReportStatus(): typeof IApiInvestigationReportStatus {
    return IApiInvestigationReportStatus;
  }

  public user: IApiUser;


  constructor(
    private router: Router,
    private location: Location,
    private documentService: DocumentService,
    private loader: LoaderService,
    private documentTypeService: DocumentTypeService,
    private reportService: InvestigationReportService,
    private notifications: NotificationsService,
    private dialogService: DialogService,
    private auth: AuthService,
    private investigationService: InvestigationService
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const data = this.router.getCurrentNavigation()?.extras?.state?.data
        if (data?.id) {
          this.investigation = data;
        }
      }
    });
  }

  ngOnInit() {
    if (this.investigation.id) {
      this.documentTypeService.get([], {
        sortOrder: SortOrder.ASC,
        orderBy: IApiDocumentTypeOrderBy.Name,
        limit: -1
      }).pipe(
        unwrapConnection(),
        map<IApiDocumentType[], IApiDocumentType[]>(types => types.sort((a, b) => a.name < b.name ? -1 : 1))
      ).subscribe((result) => {
        this.reportTypes = sortReportTypes(result);
      });
      this.getDocuments();
    }
    this.auth.authenticatedUser.pipe(
      take(1)
    ).subscribe(user => this.user = user);
  }

  public getDocuments() {
    this.loader.show$(
      forkJoin({
        reports: this.documentService.get([{
          type: IApiDocumentFilterType.Investigation,
          value: this.investigation.id
        }, {
          type: IApiDocumentFilterType.Category,
          value: DocumentCategories.REPORT.valueOf()
        }], { orderBy: IApiDocumentOrderBy.Title, sortOrder: SortOrder.ASC, limit: -1 }).pipe(
          unwrapConnection()
        )
      })
    ).subscribe(({ reports }) => {
      this.reports = reports;
    });
  }

  public async createReport(reportData = null) {
    try {
      await this.loader.show$(
        this.reportService.create({
          investigationId: this.investigation.id,
          documentTypeId: this.selectedReportTypeId,
          reportData: reportData?.length ? JSON.stringify(reportData) : ''
        })
      ).toPromise();

      this.getDocuments();
      this.selectedReportTypeId = null;
    }
    catch (exc) {
      console.error(exc);
      this.selectedReportTypeId = null;
      this.notifications.error("Error creating report. Please try again.");
    }
  }

  public async prefillModal() {
    if (this.requiredPreFillReport) {
      const dialog: DialogRef = this.dialogService.open({
        width: '95%',
        content: ReportPrefillComponent,
        preventAction: (ev) => {
          return ev !== 'closed' as any;
        },
      });

      const dialogInstance = dialog.content.instance as ReportPrefillComponent;
      dialogInstance.reportType = this.reportTypes?.find(report => report.id === this.selectedReportTypeId);
      dialog.result.subscribe((result: any) => {
        if (result?.cancel === false)
          this.createReport(result?.data);
      })
    } else {
      this.createReport();
    }

  }

  public close(): void {
    this.drawer.toggle(false);
    this.addBodyClass(false);
    this.location.back();
  }

  public addBodyClass(isOpen = true) {
    if (isOpen) {
      document.body.classList.add("kendo-dialog-open");
    } else {
      document.body.classList.remove("kendo-dialog-open");
    }
  }

  private updateInvestigationHistoryWithComment() {
    // Trigger comment dialog and, then update investigation with History attribute
    const { id } = this.investigation;
    const data = {
      notes: "",
      title: "Please add a comment.",
      original: ""
    };

    const dialog: DialogRef = this.dialogService.open({
      content: NotesModalComponent,
      width: "90%",
      preventAction: (ev) => {
        return ev !== 'closed' as any;
      },
    });

    const userInfo = dialog.content.instance as NotesModalComponent;
    userInfo.data = data;

    return dialog.result.pipe(
      switchMap((comment: any) => {
        if (typeof comment === 'string') return this.loader.show$(this.investigationService.update({ id, History: { InvestigationId: id, updateType: IApiInvestigationUpdateTypes.Delete, updateCategory: IApiInvestigationUpdateCategories.Documents, comment } })).pipe(
          this.notifications.snackbarErrorPipe()
        );
        else return of(false);
      }),
    );
  }

  public deleteDraft(report) {
    this.notifications.kendoMobileConfirm(
      `Are you sure you want to delete this draft report? All information for this report will be removed.`,
      `Delete ${report?.title}?`,
      "No, Don’t Delete",
      "Yes, Delete",
      "100%",
      true,
      "",
      "p-16",
      true
    ).subscribe((res) => {
      if (res === true) {
        this.updateInvestigationHistoryWithComment().subscribe(res => {
          if (res) {
            this.loader.show$(this.documentService.remove(report?.id).pipe(
              this.notifications.snackbarErrorPipe("Error deleting report"),
              this.notifications.snackbarPipe("Report deleted successfully.")
            )).subscribe(() => {
              this.getDocuments()
            });
          }
        })
      }
    }, err => {
      console.log(err)
    });
  }
}
